import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F8F8F8;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  padding: 48px;
  width: 100%;
  max-width: 440px;
  animation: slideIn 0.4s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;

  h1 {
    color: #F1AF59;
    font-size: 32px;
    font-weight: bold;
    margin: 0 0 8px 0;
  }

  p {
    color: #6b7280;
    font-size: 14px;
  }
`;

const LoginButton = styled.button`
  background: #F1AF59;
  color: white;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(241, 175, 89, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoNote = styled.div`
  background: #fef3e2;
  color: #92610b;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #fcd49a;
  margin-top: 20px;
  text-align: center;
  line-height: 1.5;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #F1AF59;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  p {
    color: #6b7280;
    font-size: 14px;
  }
`;

const PortalLogin = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  // If already authenticated, redirect to portal
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/portal');
    }
  }, [auth.isAuthenticated, navigate]);

  const handleLogin = () => {
    // Redirect to Cognito hosted UI
    auth.signinRedirect();
  };

  const clearAuthState = useCallback(() => {
    // Clear OIDC stale state from storage to fix cookie/session issues
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('oidc.')) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));

    const localKeysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('oidc.')) localKeysToRemove.push(key);
    }
    localKeysToRemove.forEach(key => localStorage.removeItem(key));

    auth.removeUser();
    window.location.reload();
  }, [auth]);

  // Show error state with clear session option
  if (auth.error) {
    return (
      <LoginContainer>
        <LoginCard>
          <Logo>
            <h1>Login Error</h1>
            <p>An error occurred during authentication</p>
          </Logo>
          <InfoNote style={{ marginTop: 0, marginBottom: '20px' }}>
            {auth.error.message}
          </InfoNote>
          <LoginButton onClick={clearAuthState}>
            Clear Session & Retry
          </LoginButton>
        </LoginCard>
      </LoginContainer>
    );
  }

  // Show loading while checking auth state
  if (auth.isLoading) {
    return (
      <LoginContainer>
        <LoginCard>
          <Logo>
            <h1>Portal Login</h1>
            <p>Welcome to Uplus Education Portal</p>
          </Logo>
          <LoadingSpinner>
            <div className="spinner" />
            <p>Checking authentication...</p>
          </LoadingSpinner>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <h1>Portal Login</h1>
          <p>Welcome to Uplus Education Portal</p>
        </Logo>

        <LoginButton onClick={handleLogin} disabled={auth.isLoading}>
          Login with Cognito
        </LoginButton>

        <InfoNote>
          You will be redirected to the secure login page.
          <br />
          Use your Cognito credentials to access the portal.
        </InfoNote>
      </LoginCard>
    </LoginContainer>
  );
};

export default PortalLogin;
